import React, { useState, Suspense, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import NexusDreamWeaveShowdown from './NexusDreamWeaveShowdown';
import LiveGameViewer from './Showdown/LiveGameViewer';
import { useAppContext } from '../AppContext';
import { createChallenge, subscribeToChallengeUpdates } from '../services/challengeService';
import LoadingSpinner from './LoadingSpinner';

// Lazy load games
const ModularStation = React.lazy(() => import('./DreamWeave/games/modular-station/ModularStation'));
const PlasmaHawk = React.lazy(() => import('./DreamWeave/games/plasma-hawk/PlasmaHawk'));
const IronColiseum = React.lazy(() => import('./DreamWeave/games/iron-coliseum/IronColiseum'));
const VoidHorizon = React.lazy(() => import('./DreamWeave/games/void-horizon/VoidHorizon'));
const AsteroidBlaster = React.lazy(() => import('./DreamWeave/games/asteroid-blaster/AsteroidBlaster'));
const NeuroMatch = React.lazy(() => import('./DreamWeave/games/neuro-match/NeuroMatch'));
const ChromaClash = React.lazy(() => import('./DreamWeave/games/chroma-clash/ChromaClash'));
const AuraEater = React.lazy(() => import('./DreamWeave/games/aura-eater/AuraEater'));
const SynapticStacking = React.lazy(() => import('./DreamWeave/SynapticStacking'));
const SynapticSerpent = React.lazy(() => import('./DreamWeave/games/synaptic-serpent/SynapticSerpent'));
const CosmicAscent = React.lazy(() => import('./DreamWeave/CosmicAscent'));
const StarlightSentinel = React.lazy(() => import('./DreamWeave/games/starlight-sentinel/StarlightSentinel'));
const HarmonicResonance = React.lazy(() => import('./DreamWeave/games/harmonic-resonance/HarmonicResonance'));

const GAME_OPTIONS = [
    { id: 'modular-station', name: 'ModularStation', icon: 'Gamepad2', description: 'Forged in the DreamWeave.' },
    { id: 'plasma-hawk', name: 'PlasmaHawk', icon: 'Gamepad2', description: 'Forged in the DreamWeave.' },
    { id: 'iron-coliseum', name: 'IronColiseum', icon: 'Gamepad2', description: 'Forged in the DreamWeave.' },
    { id: 'void-horizon', name: 'VoidHorizon', icon: 'Gamepad2', description: 'Forged in the DreamWeave.' },
    { id: 'asteroid-blaster', name: 'AsteroidBlaster', icon: 'Gamepad2', description: 'Forged in the DreamWeave.' },
    { id: 'neuro-match', name: 'NeuroMatch', icon: 'Gamepad2', description: 'Forged in the DreamWeave.' },
    { id: 'celestial', name: 'Celestial Showdown', icon: 'Swords', description: 'Classic Tug-of-War' },
    { id: 'auraEater', name: 'Aura Eater', icon: 'Flame', description: 'Collect energy, avoid spirits.' },
    { id: 'starlightSentinel', name: 'Starlight Sentinel', icon: 'Shield', description: 'Survive the bullet hell.' },
    { id: 'harmonicResonance', name: 'Harmonic Resonance', icon: 'Music', description: 'Rhythm of the Cosmos.' },
    { id: 'synapticSerpent', name: 'Synaptic Serpent', icon: 'Spline', description: 'Classic Snake with a twist.' },
    { id: 'synapticStacking', name: 'Synaptic Stacking', icon: 'Layers', description: 'Stack blocks to survive.' },
    { id: 'cosmicAscent', name: 'Cosmic Ascent', icon: 'Rocket', description: 'Jump to the stars.' },
];

const ShowdownModal = ({ onClose, rivalId = null, challengeMode = false, initialGameType = null }) => {
    const { currentUserProfile, activeNexusId, handlePageChange } = useAppContext();
    const [selectedGame, setSelectedGame] = useState(initialGameType || null);
    const [gameActive, setGameActive] = useState(false);
    const [challengeSent, setChallengeSent] = useState(false);
    const [createdChallengeId, setCreatedChallengeId] = useState(null);
    
    const targetNexusId = activeNexusId || currentUserProfile?.primaryNexusId || 'global_arena';

    const handleGameSelect = (gameId) => {
        setSelectedGame(gameId);
    };

    // Listen for acceptance of the created challenge
    useEffect(() => {
        if (!createdChallengeId) return;

        const unsubscribe = subscribeToChallengeUpdates(createdChallengeId, (challengeData) => {
            if (challengeData.status === 'accepted') {
                // Redirect to the Showdown Page with the active challenge
                handlePageChange('showdown', { challengeId: createdChallengeId });
                onClose(); 
            } else if (challengeData.status === 'rejected') {
                // Handle rejection (optional: show toast or update UI)
                setChallengeSent(false); // Reset UI
                setCreatedChallengeId(null);
            }
        });

        return () => unsubscribe();
    }, [createdChallengeId, handlePageChange, onClose]);

    const handleInitiate = async () => {
        if (challengeMode && rivalId) {
            // Send challenge via Firestore
            const result = await createChallenge(currentUserProfile, rivalId, selectedGame);
            if (result.success) {
                setChallengeSent(true);
                setCreatedChallengeId(result.challengeId); // Start listening
            }
        } else {
            // Play against AI immediately
            setGameActive(true);
        }
    };

    const renderPlayerGame = () => {
        const commonProps = {
            nexusId: targetNexusId,
            currentUser: currentUserProfile,
            rivalId: rivalId,
            onClose: onClose,
            isSpectatorMode: false, 
            survivalMode: true 
        };

        switch (selectedGame) {
            case 'celestial':
                return <NexusDreamWeaveShowdown {...commonProps} />;
            case 'auraEater':
                return <Suspense fallback={<LoadingSpinner />}><AuraEater {...commonProps} /></Suspense>;
            case 'starlightSentinel':
                return <Suspense fallback={<LoadingSpinner />}><StarlightSentinel {...commonProps} /></Suspense>;
            case 'harmonicResonance':
                return <Suspense fallback={<LoadingSpinner />}><HarmonicResonance {...commonProps} autoStart={true} /></Suspense>;
            case 'synapticSerpent':
                return <Suspense fallback={<LoadingSpinner />}><SynapticSerpent {...commonProps} /></Suspense>;
            case 'synapticStacking':
                return <Suspense fallback={<LoadingSpinner />}><SynapticStacking {...commonProps} /></Suspense>;
            case 'cosmicAscent':
                return <Suspense fallback={<LoadingSpinner />}><CosmicAscent {...commonProps} /></Suspense>;
            case 'chroma-clash':
                return <Suspense fallback={<LoadingSpinner />}><ChromaClash {...commonProps} /></Suspense>;
            case 'neuro-match':
                return <Suspense fallback={<LoadingSpinner />}><NeuroMatch {...commonProps} /></Suspense>;
            case 'asteroid-blaster':
                return <Suspense fallback={<LoadingSpinner />}><AsteroidBlaster {...commonProps} /></Suspense>;
            case 'void-horizon':
                return <Suspense fallback={<LoadingSpinner />}><VoidHorizon {...commonProps} /></Suspense>;
            case 'iron-coliseum':
                return <Suspense fallback={<LoadingSpinner />}><IronColiseum {...commonProps} /></Suspense>;
            case 'plasma-hawk':
                return <Suspense fallback={<LoadingSpinner />}><PlasmaHawk {...commonProps} /></Suspense>;
            case 'modular-station':
                return <Suspense fallback={<LoadingSpinner />}><ModularStation {...commonProps} /></Suspense>;
            default:
                return null;
        }
    };

    if (gameActive) {
        // Split View: Player vs AI Spectator
        return (
            <div className="fixed inset-0 z-[100] bg-black flex flex-col md:flex-row">
                {/* Player Side (Top/Left) */}
                <div className="flex-1 relative border-r border-gray-700">
                    <button 
                        onClick={onClose} 
                        className="absolute top-4 left-4 z-50 p-2 bg-black/40 rounded-full text-white hover:bg-white/20"
                    >
                        <LucideIcons.ArrowLeft size={24} />
                    </button>
                    {renderPlayerGame()}
                </div>

                {/* AI/Opponent Side (Bottom/Right) */}
                {/* For now, if it's not 'celestial' (which is self-contained), show the AI playing the same game */}
                {selectedGame !== 'celestial' && (
                    <div className="h-1/3 md:h-full md:w-1/3 border-t md:border-t-0 md:border-l border-gray-700 relative">
                        <div className="absolute inset-0 pointer-events-none z-10 flex flex-col items-center justify-center">
                            {/* Overlay to indicate this is the AI view */}
                        </div>
                        {/* Reuse LiveGameViewer for Demo/AI View */}
                        <LiveGameViewer 
                            gameType={selectedGame}
                            souls={currentUserProfile?.souls || 0} 
                        />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="modal-overlay fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div 
                className="relative w-full max-w-2xl h-[85vh] bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-gray-700 flex flex-col transform scale-100 opacity-100 transition-all duration-300 animate-in fade-in zoom-in-95"
            >
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 z-20 p-2 bg-black/40 rounded-full text-white hover:bg-white/20 transition-colors"
                >
                    <LucideIcons.X size={24} />
                </button>

                <div className="flex flex-col h-full p-6">
                    <div className="text-center mb-6">
                        <h2 className="text-3xl font-bold text-white mb-2">
                            {challengeMode ? "Issue Challenge" : "Enter the Arena"}
                        </h2>
                        <p className="text-gray-400">
                            {challengeMode 
                                ? "Select a discipline to challenge your rival." 
                                : "Train against the DreamWeave AI."}
                        </p>
                    </div>

                    {!selectedGame ? (
                        <div className="flex-1 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-4 p-2">
                            {GAME_OPTIONS.map((game) => {
                                const Icon = LucideIcons[game.icon] || LucideIcons.Gamepad2;
                                return (
                                    <button
                                        key={game.id}
                                        onClick={() => handleGameSelect(game.id)}
                                        className="flex flex-col items-center p-6 bg-gray-800/50 hover:bg-gray-700/80 border border-gray-700 hover:border-cyan-500 rounded-xl transition-all group"
                                    >
                                        <div className="w-16 h-16 rounded-full bg-gray-900 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform border border-gray-600 group-hover:border-cyan-400">
                                            <Icon className="w-8 h-8 text-gray-400 group-hover:text-cyan-400" />
                                        </div>
                                        <h3 className="text-white font-bold text-lg">{game.name}</h3>
                                        <p className="text-sm text-gray-500 text-center mt-1">{game.description}</p>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center space-y-8">
                            {!challengeSent ? (
                                <>
                                    <div className="text-center animate-fadeIn">
                                        <div className="w-24 h-24 mx-auto bg-gradient-to-br from-purple-600 to-cyan-500 rounded-full flex items-center justify-center shadow-lg shadow-cyan-500/20 mb-6">
                                            {(() => {
                                                const game = GAME_OPTIONS.find(g => g.id === selectedGame);
                                                const Icon = (game && LucideIcons[game.icon]) || LucideIcons.Gamepad2;
                                                return <Icon size={48} className="text-white" />;
                                            })()}
                                        </div>
                                        <h3 className="text-2xl font-bold text-white mb-2">
                                            {GAME_OPTIONS.find(g => g.id === selectedGame)?.name}
                                        </h3>
                                        <p className="text-cyan-400 font-mono text-sm uppercase tracking-widest">
                                            Mode: Survival
                                        </p>
                                    </div>

                                    <div className="w-full max-w-md space-y-3">
                                        <button 
                                            onClick={handleInitiate}
                                            className="w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold text-lg rounded-full shadow-lg hover:shadow-cyan-500/50 transition-all transform hover:scale-105 active:scale-95"
                                        >
                                            {challengeMode ? "Send Challenge" : "Start Training"}
                                        </button>
                                        <button 
                                            onClick={() => setSelectedGame(null)}
                                            className="w-full py-3 text-gray-400 hover:text-white font-medium transition-colors"
                                        >
                                            Change Game
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center animate-fadeIn flex flex-col items-center">
                                    <div className="w-20 h-20 rounded-full border-4 border-t-cyan-500 border-r-cyan-500/50 border-b-transparent border-l-transparent animate-spin mb-6" />
                                    <h3 className="text-xl font-bold text-white mb-2">Challenge Sent</h3>
                                    <p className="text-gray-400">Waiting for opponent to accept...</p>
                                    <button 
                                        onClick={onClose}
                                        className="mt-8 px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm"
                                    >
                                        Run in Background
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShowdownModal;